import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { MsalAuthService, MsalConfig } from './msal-auth.service';

@Injectable({
  providedIn: 'root',
})
export class MsalService {
  currentMsalConfig: MsalConfig | null = null;
  private loadingSubject = new BehaviorSubject<boolean>(false);

  loading$ = this.loadingSubject.asObservable();

  // Get tokens from the auth service, using the acquireTokenSilent method
  tokens$ = this.msalAuthService.tokens$.pipe(
    filter(
      (tokens): tokens is { accessToken: string; idToken: string } =>
        tokens !== null,
    ),
    map((tokens) => ({
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
    })),
    shareReplay(1),
  );

  constructor(private msalAuthService: MsalAuthService) {}

  setMsalConfig(config: MsalConfig): void {
    this.currentMsalConfig = config;
    // Initialize MSAL with the config
    this.msalAuthService.initializeMsal(config);
  }

  login(): void {
    if (!this.currentMsalConfig) {
      console.error('MSAL configuration not set. Call setMsalConfig() first.');
      return;
    }

    this.loadingSubject.next(true);
    this.msalAuthService.login();
  }

  logout(): void {
    this.msalAuthService.logout();
    this.loadingSubject.next(false);
  }

  getCurrentTokens(): { accessToken: string; idToken: string } | null {
    return this.msalAuthService.getCurrentTokens();
  }

  // Method to get accounts (useful for checking login state)
  getAllAccounts() {
    return this.msalAuthService.getAllAccounts();
  }

  getActiveAccount() {
    return this.msalAuthService.getActiveAccount();
  }

  // Method to manually acquire tokens
  acquireTokenSilent(): Observable<{ accessToken: string; idToken: string }> {
    return this.msalAuthService.acquireTokenSilentObservable();
  }
}
